// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Import Firebase services needed directly
import { db, storage } from '../firebase';
import { collection, query, where, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';

// Import date-fns functions
import { format, getMonth } from 'date-fns';

// Import Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';

// Import react-bootstrap
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';

// Import the Auth Context hook and components
import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar';

// --- Helper to format Month/Day verbosely ---
const formatVerboseDate = (monthNumStr, dayNumStr) => {
    const monthNum = parseInt(monthNumStr, 10);
    const dayNum = parseInt(dayNumStr, 10);
    if (isNaN(monthNum) || isNaN(dayNum) || monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
       console.warn("Invalid month/day strings for formatting:", monthNumStr, dayNumStr);
       return `${monthNumStr || '?'}/${dayNumStr || '?'}`;
    }
    try {
        return format(new Date(2000, monthNum - 1, dayNum), 'MMMM d');
    } catch (e) {
        console.error("Error formatting verbose date:", e);
        return `${monthNum}/${dayNum}`;
    }
};

// --- Main Component: Dashboard ---
function Dashboard() {
  const { currentUser, userOrgId, loadingAuth } = useAuth();

  const [counts, setCounts] = useState({ families: 0, members: 0, groups: 0, lessons: 0 });
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [showMobileAd, setShowMobileAd] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loadingAuth || !userOrgId) {
        if (!loadingAuth && currentUser && !userOrgId) {
            setError("Organization details missing from your account. Cannot load dashboard.");
            setLoading(false);
        }
        if (!loadingAuth) {
           setLoading(false);
        }
        return;
    }

    setLoading(true);
    setError(null);
    let isMounted = true;

    const fetchData = async () => {
      try {
        const currentMonth = getMonth(new Date()) + 1;
        const currentMonthString = String(currentMonth);

        const fetchCounts = async () => {
            const familiesQuery = query(collection(db, 'family'), where('orgId', '==', userOrgId));
            const membersQuery = query(collection(db, 'member'), where('orgId', '==', userOrgId));
            const groupsQuery = query(collection(db, 'group'), where('orgId', '==', userOrgId));
            const lessonsQuery = query(collection(db, 'lesson'), where('orgId', '==', userOrgId), where('visibility', '==', 'public'));
            const [fSnap, mSnap, gSnap, lSnap] = await Promise.all([getDocs(familiesQuery), getDocs(membersQuery), getDocs(groupsQuery), getDocs(lessonsQuery)]);
            return { families: fSnap.size, members: mSnap.size, groups: gSnap.size, lessons: lSnap.size };
        };

        const fetchMobileStatus = async () => {
            if (!currentUser) return true;
            try {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                return !(userDocSnap.exists() && userDocSnap.data().mobileAppLinked);
            } catch(userDocError) {
                console.warn("Dashboard: Could not check user document for mobile status", userDocError);
            }
            return true;
        };

        const fetchBirthdays = async () => {
            const bdayQuery = query(collection(db, 'member'), where('orgId', '==', userOrgId), where('birthday_month', '==', currentMonthString));
            const bdaySnap = await getDocs(bdayQuery);
            const birthdayList = bdaySnap.docs.map(d => {
                 const data = d.data();
                 const monthStr = data.birthday_month;
                 const dayStr = data.birthday_day;
                 return {
                    id: d.id,
                    familyId: data.familyId || '', // ** ADDED familyId to link to member page **
                    name: data.name || '',
                    surname: data.surname || '',
                    verboseBirthday: formatVerboseDate(monthStr, dayStr),
                    dayOfMonth: parseInt(dayStr, 10) || 0,
                    photo: data.photo,
                    imageUpdatedAt: data.imageUpdatedAt
                };
             });
             birthdayList.sort((a, b) => a.dayOfMonth - b.dayOfMonth);
             return birthdayList;
        };

        const fetchAnniversaries = async () => {
            const annivQuery = query(collection(db, 'family'), where('orgId', '==', userOrgId), where('anniversary_month', '==', currentMonthString));
            const annivSnap = await getDocs(annivQuery);
            const anniversaryListPromises = annivSnap.docs.map(async (d) => {
                const familyData = d.data();
                const monthStr = familyData.anniversary_month;
                const dayStr = familyData.anniversary_day;
                let photoURL = '/assets/blank.png';
                if (familyData.photo) {
                    try {
                        photoURL = familyData.photo.startsWith('http') ? familyData.photo : await getDownloadURL(ref(storage, familyData.photo));
                    }
                    catch (e) { if (e.code !== 'storage/object-not-found') { console.error(`Error getting anniversary photo ${familyData.photo}:`, e); } }
                }
                return { id: d.id, surname: familyData.surname || '', verboseAnniversary: formatVerboseDate(monthStr, dayStr), dayOfMonth: parseInt(dayStr, 10) || 0, photoURL: photoURL };
            });
            const anniversaryList = await Promise.all(anniversaryListPromises);
            anniversaryList.sort((a, b) => a.dayOfMonth - b.dayOfMonth);
            return anniversaryList;
        };

        const [countResults, mobileStatus, birthdayResults, anniversaryResults] = await Promise.all([
            fetchCounts(),
            fetchMobileStatus(),
            fetchBirthdays(),
            fetchAnniversaries()
        ]);

        if (isMounted) {
            setCounts(countResults);
            setShowMobileAd(mobileStatus);
            setBirthdays(birthdayResults);
            setAnniversaries(anniversaryResults);
            setError(null);
        }

      } catch (err) {
        console.error("Dashboard: Error fetching data:", err);
        if (isMounted) { setError(err.message || "Failed to load dashboard data."); }
      } finally {
        if (isMounted) { setLoading(false); }
      }
    };

    fetchData();

    return () => { isMounted = false; };
  }, [userOrgId, loadingAuth, currentUser]);


  if (loadingAuth) {
    return ( <Container className="text-center mt-5"><Spinner animation="border" /><p className="mt-2">Loading User...</p></Container> );
  }
  if (!currentUser) {
       return ( <Container className="mt-3"><Alert variant="warning">Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view the dashboard.</Alert></Container> );
  }
  if (loading) {
    return ( <Container className="text-center mt-5"><Spinner animation="border" /><p className="mt-2">Loading Dashboard Data...</p></Container> );
  }
  if (error) {
    return ( <Container className="mt-3"><Alert variant="danger">Error: {error}</Alert></Container> );
  }

  return (
    <Container fluid className="mt-3">
      {/* Counts and Mobile Ad Row */}
      <Row className="g-3">
        {/* Count Cards */}
        <Col xs={6} md={4} lg={2}>
          <Card text="dark" bg="light" className="text-center h-100 shadow-sm">
            <Card.Header className="text-white bg-primary">Families</Card.Header>
            <Card.Body className="d-flex align-items-center justify-content-center">
              <Link to="/families" className="text-decoration-none text-dark stretched-link">
                 <h5 className="card-title display-5 fw-bold">{counts.families}</h5>
              </Link>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <Card text="dark" bg="light" className="text-center h-100 shadow-sm">
             <Card.Header className="text-white bg-primary">Members</Card.Header>
             <Card.Body className="d-flex align-items-center justify-content-center">
                 <h5 className="card-title display-5 fw-bold">{counts.members}</h5>
             </Card.Body>
          </Card>
        </Col>
         <Col xs={6} md={4} lg={2}>
          <Card text="dark" bg="light" className="text-center h-100 shadow-sm">
             <Card.Header className="text-white bg-primary">Groups</Card.Header>
              <Card.Body className="d-flex align-items-center justify-content-center">
                 <h5 className="card-title display-5 fw-bold">{counts.groups}</h5>
             </Card.Body>
          </Card>
        </Col>
         <Col xs={6} md={4} lg={2}>
          <Card text="dark" bg="light" className="text-center h-100 shadow-sm">
             <Card.Header className="text-white bg-primary">Lessons</Card.Header>
              <Card.Body className="d-flex align-items-center justify-content-center">
                 <h5 className="card-title display-5 fw-bold">{counts.lessons}</h5>
             </Card.Body>
          </Card>
        </Col>
        {/* Mobile Ad Card */}
        {showMobileAd && (
          <Col xs={12} md={8} lg={4}>
            <Card className="text-center h-100 shadow-sm">
              <Card.Header className="text-white d-flex justify-content-between align-items-center" style={{ backgroundColor: '#45505d' }}>
                <span> <FontAwesomeIcon icon={faInfoCircle} className="me-1" /> Church Aware Mobile App </span>
                 <span> <FontAwesomeIcon icon={faAndroid} style={{ color: '#40c130' }} className="me-1" /> <FontAwesomeIcon icon={faApple} style={{ color: '#cacbcc' }} /> </span>
              </Card.Header>
              <Card.Body>
                <Card.Title as="h6">Search 'Church Aware' in your App Store</Card.Title>
                <div style={{ fontSize: '14px' }}>
                    <FontAwesomeIcon icon={faAndroid} className="me-1"/> <a href="https://play.google.com/store/apps/details?id=com.churchaware.churchaware" target="_blank" rel="noopener noreferrer">Android</a>
                    <FontAwesomeIcon icon={faApple} className="ms-2 me-1" /> <a href="https://apps.apple.com/us/app/church-aware/id1422942877?ls=1" target="_blank" rel="noopener noreferrer">Apple</a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Birthdays and Anniversaries Row */}
      <Row className="g-3 mt-3">
        {/* Birthdays Card */}
        <Col md={6}>
          <Card className="text-center h-100 shadow-sm">
             <Card.Header className="text-white" style={{backgroundColor: '#5d6166'}}> Birthdays This Month </Card.Header>
             <Card.Body>
                <Row>
                  {birthdays.length > 0 ? (
                    birthdays.map(member => {
                        const photoCacheKey = member.imageUpdatedAt instanceof Timestamp
                                              ? member.imageUpdatedAt.toMillis().toString()
                                              : (member.imageUpdatedAt || member.id);
                        return (
                            <Col xs={6} lg={4} className="mb-3 text-center" key={member.id}>
                                <Link to={`/families/${member.familyId}/member/${member.id}`} className="text-decoration-none text-dark">
                                    <MemberPhotoAvatar
                                        src={member.photo}
                                        alt={`${member.name} ${member.surname}`}
                                        size={75}
                                        cacheKey={photoCacheKey}
                                    />
                                    <small className="d-block lh-sm mt-1"> {member.name} {member.surname} </small>
                                    <small className="d-block text-muted"> ({member.verboseBirthday}) </small>
                                </Link>
                            </Col>
                        );
                    })
                  ) : (
                    <p className="text-muted mt-3">No members with birthdays this month.</p>
                  )}
                </Row>
             </Card.Body>
          </Card>
        </Col>
        {/* Anniversaries Card */}
        <Col md={6}>
          <Card className="text-center h-100 shadow-sm">
             <Card.Header className="text-white" style={{backgroundColor: '#5d6166'}}> Anniversaries This Month </Card.Header>
             <Card.Body>
                 <Row>
                    {anniversaries.length > 0 ? (
                        anniversaries.map(family => (
                            <Col xs={6} lg={4} className="mb-3 text-center" key={family.id}>
                                <Link to={`/families/${family.id}`} className="text-decoration-none text-dark">
                                    <img className="img-fluid rounded mb-1 object-fit-cover" src={family.photoURL} alt={`${family.surname} Anniversary`} style={{ height: '75px', width: '75px', border: '1px solid #dee2e6' }} onError={(e) => { e.target.onerror = null; e.target.src='/assets/blank.png'; }}/> <br />
                                    <small className="d-block lh-sm"> {family.surname} Family </small>
                                    <small className="d-block text-muted"> ({family.verboseAnniversary}) </small>
                                </Link>
                            </Col>
                        ))
                    ) : (
                         <p className="text-muted mt-3">No families with anniversaries this month.</p>
                    )}
                </Row>
             </Card.Body>
           </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Dashboard;
